import type { Accion, Apunte, Contacto, Etapa, Obra, TipoApunte } from './modelo.js';

/**
 * El estado del modulo.
 *
 * EL REDUCTOR SIGUE SIENDO PURO. Guarda en el servidor quien lo llama
 * (`useObras`), no esto: asi las pruebas pueden reproducir veinte movimientos
 * seguidos sin red, y la pantalla puede pintar el cambio antes de que la
 * peticion vuelva.
 *
 * Cada cambio deja un apunte en el historial. No hay forma de mover una
 * oportunidad sin que quede escrito quien la movio y cuando: en un CRM que
 * comparten varias personas, «esto estaba asi cuando llegue» tiene que poder
 * comprobarse.
 */

export interface Estado {
  obras: Obra[];
  /** Ultima conversion simulada, para poder ensenarla y deshacerla. */
  ultimaConversion: { obraId: string; cliente: string; propiedad: string } | null;
}

/**
 * SE ARRANCA VACIO, y lo que hay llega del servidor.
 *
 * Antes entraban veinte obras inventadas para poder ensenar el modulo
 * funcionando. Desde que se cargan obras de verdad no pueden estar: mezcladas
 * con las reales hay que distinguirlas a ojo, y basta un despiste para llamar a
 * un promotor que no existe. Siguen en `datos-de-prueba.ts`, que solo leen las
 * pruebas.
 */
export function estadoInicial(): Estado {
  return { obras: [], ultimaConversion: null };
}

export type Accion_ =
  | { tipo: 'cargar'; obras: Obra[] }
  | { tipo: 'crear'; obra: Obra }
  | { tipo: 'editar'; obraId: string; cambios: Partial<Obra>; quien: string; apunte?: string }
  | { tipo: 'etapa'; obraId: string; etapa: Etapa; quien: string; comentario: string }
  | {
      tipo: 'progreso';
      obraId: string;
      fase: Obra['fase'];
      progreso: number;
      confianza: Obra['confianza'];
      finalizacion: string | null;
      quien: string;
      comentario: string;
    }
  | { tipo: 'comprobar'; obraId: string; quien: string; nota: string }
  | { tipo: 'contactoAlta'; obraId: string; contacto: Contacto; quien: string }
  | { tipo: 'contactoBaja'; obraId: string; contactoId: string; quien: string }
  | { tipo: 'accionAlta'; obraId: string; accion: Accion; quien: string }
  | { tipo: 'accionHecha'; obraId: string; accionId: string; quien: string }
  | { tipo: 'seguimiento'; obraId: string; clase: TipoApunte; texto: string; quien: string }
  | { tipo: 'convertir'; obraId: string; cliente: string; propiedad: string; quien: string }
  | { tipo: 'reiniciar' };

let contador = 0;
const nuevoId = (p: string) => `${p}-l${(contador += 1)}-${Date.now().toString(36)}`;

function ahora(): string {
  return new Date().toISOString().slice(0, 16);
}

function apuntar(obra: Obra, tipo: TipoApunte, quien: string, texto: string): Obra {
  const apunte: Apunte = { id: nuevoId('ap'), tipo, cuando: ahora(), quien, texto };
  return { ...obra, historial: [apunte, ...obra.historial] };
}

function mapear(estado: Estado, obraId: string, fn: (o: Obra) => Obra): Estado {
  return { ...estado, obras: estado.obras.map((o) => (o.id === obraId ? fn(o) : o)) };
}

export function reducir(estado: Estado, accion: Accion_): Estado {
  switch (accion.tipo) {
    case 'reiniciar':
      return estadoInicial();

    case 'cargar':
      return { ...estado, obras: accion.obras };

    case 'crear':
      return { ...estado, obras: [accion.obra, ...estado.obras] };

    case 'editar':
      return mapear(estado, accion.obraId, (o) => {
        const cambiada = { ...o, ...accion.cambios };
        return accion.apunte ? apuntar(cambiada, 'NOTA', accion.quien, accion.apunte) : cambiada;
      });

    case 'etapa':
      return mapear(estado, accion.obraId, (o) => {
        if (o.etapa === accion.etapa) return o;
        const texto = accion.comentario.trim();
        const cambiada: Obra = {
          ...o,
          etapa: accion.etapa,
          // El motivo solo se guarda si la etapa es «perdida»: arrastrarlo a
          // una etapa viva contaria una historia que ya no es cierta.
          motivoPerdida: accion.etapa === 'PERDIDA' ? texto : null,
        };
        return apuntar(cambiada, 'ETAPA', accion.quien, texto);
      });

    case 'progreso':
      return mapear(estado, accion.obraId, (o) => {
        const cambiada: Obra = {
          ...o,
          fase: accion.fase,
          progreso: accion.progreso,
          confianza: accion.confianza,
          finalizacionEstimada: accion.finalizacion,
          // Cambiar el progreso ES comprobarlo: la fecha se actualiza sola,
          // porque si no queda un progreso nuevo con una fecha vieja al lado.
          ultimaComprobacion: ahora().slice(0, 10),
          comprobadaPor: accion.quien,
          comentarioObra: accion.comentario.trim() === '' ? o.comentarioObra : accion.comentario,
        };
        return apuntar(cambiada, 'PROGRESO', accion.quien, accion.comentario);
      });

    case 'comprobar':
      return mapear(estado, accion.obraId, (o) =>
        apuntar(
          {
            ...o,
            ultimaComprobacion: ahora().slice(0, 10),
            comprobadaPor: accion.quien,
          },
          'PROGRESO',
          accion.quien,
          accion.nota,
        ),
      );

    case 'contactoAlta':
      return mapear(estado, accion.obraId, (o) =>
        apuntar(
          { ...o, contactos: [...o.contactos, accion.contacto] },
          'CONTACTO',
          accion.quien,
          accion.contacto.nombre,
        ),
      );

    case 'contactoBaja':
      return mapear(estado, accion.obraId, (o) => {
        const fuera = o.contactos.find((c) => c.id === accion.contactoId);
        return apuntar(
          { ...o, contactos: o.contactos.filter((c) => c.id !== accion.contactoId) },
          'CONTACTO',
          accion.quien,
          fuera?.nombre ?? '',
        );
      });

    case 'accionAlta':
      return mapear(estado, accion.obraId, (o) =>
        apuntar(
          { ...o, acciones: [...o.acciones, accion.accion] },
          'TAREA',
          accion.quien,
          accion.accion.descripcion,
        ),
      );

    case 'accionHecha':
      return mapear(estado, accion.obraId, (o) => {
        const hecha = o.acciones.find((a) => a.id === accion.accionId);
        return apuntar(
          {
            ...o,
            acciones: o.acciones.map((a) => (a.id === accion.accionId ? { ...a, hecha: true } : a)),
          },
          'TAREA',
          accion.quien,
          hecha?.descripcion ?? '',
        );
      });

    case 'seguimiento':
      return mapear(estado, accion.obraId, (o) =>
        apuntar(o, accion.clase, accion.quien, accion.texto),
      );

    case 'convertir':
      return {
        ...mapear(estado, accion.obraId, (o) =>
          apuntar(
            {
              ...o,
              etapa: 'GANADA',
              convertidaEn: {
                clienteId: accion.cliente,
                propiedadId: accion.propiedad,
                cuando: ahora(),
              },
            },
            'CONVERSION',
            accion.quien,
            `${accion.cliente} · ${accion.propiedad}`,
          ),
        ),
        ultimaConversion: {
          obraId: accion.obraId,
          cliente: accion.cliente,
          propiedad: accion.propiedad,
        },
      };
  }
}

/** Una obra vacia con lo minimo, para empezar a rellenar el formulario. */
export function obraNueva(): Obra {
  return {
    // UUID y no un contador: la ficha ya tiene su identidad antes de llegar al
    // servidor, y la pantalla no espera a la respuesta para seguir editandola.
    id: crypto.randomUUID(),
    codigo: '',
    nombre: '',
    direccion: '',
    codigoPostal: '',
    municipio: '',
    zona: '',
    lat: 39.6,
    lon: 2.9,
    tipoProyecto: 'VILLA',
    fase: 'DESCONOCIDO',
    progreso: 0,
    finalizacionEstimada: null,
    confianza: 'BAJA',
    ultimaComprobacion: null,
    comprobadaPor: null,
    comentarioObra: null,
    etapa: 'DETECTADA',
    responsable: null,
    valorMercadoCents: null,
    potencialCents: null,
    origen: null,
    etiquetas: [],
    motivoPerdida: null,
    situacion: {
      propietarioConocido: null,
      usoDelPropietario: null,
      saldraAVenta: null,
      yaEnVenta: null,
    },
    contactos: [],
    acciones: [],
    historial: [],
    convertidaEn: null,
  };
}

export const idDe = nuevoId;
